import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code and password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
      toast.success('Reset code sent! Please check your email.');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Show success message anyway for security (don't reveal if email exists)
      setStep(2);
      toast.success('If that email exists, a reset code has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code: code.toUpperCase(),
        password: newPassword
      });
      toast.success('Password reset successfully! You can now login.');
      navigate('/');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-24 md:h-32 w-auto" />
          </div>
        </div>

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl md:text-2xl" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {step === 1 ? 'Forgot Password' : 'Reset Your Password'}
            </CardTitle>
            <CardDescription className="text-sm">
              {step === 1 
                ? "Enter your email to receive a 6-digit reset code"
                : "Enter the code from your email and your new password"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#000000] hover:bg-[#333333] text-white"
                >
                  {loading ? 'Sending Code...' : 'Send Reset Code'}
                </Button>

                <div className="text-center">
                  <a
                    href="/"
                    className="inline-flex items-center text-[#000000] hover:underline font-medium"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </a>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-800 text-sm font-medium">Code sent to {email}</p>
                      <p className="text-blue-700 text-xs mt-1">
                        Check your email for a 6-digit code (valid for 15 minutes)
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="code">6-Digit Reset Code</Label>
                  <Input
                    id="code"
                    name="code"
                    type="text"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-digit code"
                    className="text-center text-2xl tracking-widest font-mono"
                    style={{ letterSpacing: '0.5em' }}
                  />
                </div>

                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#000000] hover:bg-[#333333] text-white"
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>

                <div className="text-center space-y-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Didn't receive code? Request new one
                  </button>
                  <div>
                    <a
                      href="/"
                      className="inline-flex items-center text-[#000000] hover:underline font-medium"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </a>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
